import { store, getCourseStats } from '../store.js';
import { openAuthModal } from './AuthModal.js';
import { authState } from '../auth.js';

export function renderCourseCatalog(container) {
  const stats = getCourseStats();
  const course = store.course;
  const modules = store.modules;
  const lessons = store.lessons;

  container.innerHTML = `
    <!-- HERO SECTION -->
    <section class="hero-section">
      <div class="hero-glow-orb"></div>
      <div class="hero-content">
        <div class="hero-badge">
          <i class="fa-solid fa-seedling"></i> Walk Together Ministries
        </div>
        <h1 class="hero-title">${course.title || 'Discipleship 101'}</h1>
        <p class="hero-desc">
          ${course.subtitle ? `<strong>${course.subtitle}</strong>. ` : ''}
          A step-by-step discipleship program designed to help you grow as a Disciple and a Disciple Maker. Built on biblical foundations, intentional community cohorts, and practical spiritual disciplines.
        </p>

        <div class="hero-actions">
          <a href="#lesson/${lessons[0]?.id || '1'}" class="btn-primary" style="font-size:1rem; padding:0.75rem 1.6rem;" id="hero-start-btn">
            <i class="fa-solid fa-play"></i> ${stats.completedLessons > 0 ? 'Resume Lesson' : 'Start Course'}
          </a>
          <button id="open-video-btn" class="btn-secondary" style="font-size:1rem; padding:0.75rem 1.4rem;">
            <i class="fa-solid fa-circle-play" style="color:var(--accent-rose);"></i> Watch Video Intro
          </button>
          <a href="#verses" class="btn-gold" style="font-size:1rem; padding:0.75rem 1.4rem;">
            <i class="fa-solid fa-layer-group"></i> Memory Verses (${stats.memorizedVerses}/${stats.totalVerses})
          </a>
        </div>

        <div class="hero-stats-row">
          <div class="stat-card">
            <div class="stat-num">${stats.totalLessons}</div>
            <div class="stat-label">Total Lessons</div>
          </div>
          <div class="stat-card">
            <div class="stat-num">${modules.length}</div>
            <div class="stat-label">Curriculum Modules</div>
          </div>
          <div class="stat-card">
            <div class="stat-num">${stats.totalVerses}</div>
            <div class="stat-label">Memory Verses</div>
          </div>
          <div class="stat-card">
            <div class="stat-num" style="color:var(--accent-emerald);">${stats.percentage}%</div>
            <div class="stat-label">Your Progress</div>
          </div>
        </div>
      </div>
    </section>

    <!-- PROGRESS BANNER -->
    <div class="progress-banner">
      <div class="progress-banner-inner">
        <div class="progress-info">
          <i class="fa-solid fa-award" style="color:var(--accent-emerald); font-size:1.4rem;"></i>
          <div>
            <div style="font-weight:600; font-size:0.95rem;">Course Completion Tracker</div>
            <div style="font-size:0.8rem; color:var(--text-secondary);">${stats.completedLessons} of ${stats.totalLessons} lessons finished</div>
          </div>
        </div>
        <div class="progress-track-wrapper">
          <div class="progress-track">
            <div class="progress-fill" style="width: ${stats.percentage}%;"></div>
          </div>
        </div>
        <div class="progress-ring-text">${stats.percentage}% Completed</div>
      </div>
    </div>

    <!-- SYLLABUS & MODULES -->
    <div class="content-container">
      <div class="section-header">
        <div>
          <h2 class="section-title">Course Syllabus & Roadmap</h2>
          <p class="section-subtitle">23 in-depth lessons divided into 5 progressive spiritual growth modules</p>
        </div>
        <div style="display:flex; gap:0.5rem;">
          <button id="expand-all-modules" class="btn-secondary" style="font-size:0.82rem; padding:0.4rem 0.8rem;">
            Expand All
          </button>
          <button id="collapse-all-modules" class="btn-secondary" style="font-size:0.82rem; padding:0.4rem 0.8rem;">
            Collapse All
          </button>
        </div>
      </div>

      <div class="modules-list" id="modules-accordion">
        ${modules.map((mod, modIdx) => {
          const modLessons = lessons.filter(l => l.module_name === mod.title || l.module_id === mod.id);
          const completedCount = modLessons.filter(l => store.progress[l.id]?.is_completed).length;
          const isAllDone = modLessons.length > 0 && completedCount === modLessons.length;
          const isOpen = modIdx === 0 || completedCount > 0; // Default open first module

          return `
            <div class="module-card ${isOpen ? 'open' : ''}" data-module="${mod.slug}" id="module-card-${mod.slug}">
              <div class="module-header" data-toggle-module="${mod.slug}">
                <div class="module-meta-left">
                  <div class="module-icon-badge">
                    <i class="fa-solid fa-${mod.icon || 'book-open'}"></i>
                  </div>
                  <div class="module-info">
                    <div class="module-step-badge">
                      Module ${modIdx + 1} of ${modules.length} • ${modLessons.length} Lessons
                      ${isAllDone ? '<span style="color:var(--accent-emerald); margin-left:8px;"><i class="fa-solid fa-check-circle"></i> Completed</span>' : ''}
                    </div>
                    <h3 class="module-title">${mod.title}</h3>
                    <p class="module-desc">${mod.description}</p>
                  </div>
                </div>

                <div style="display:flex; align-items:center; gap:1rem;">
                  <span style="font-size:0.85rem; font-weight:600; color:var(--text-secondary);">
                    ${completedCount}/${modLessons.length} Done
                  </span>
                  <div class="module-toggle-btn">
                    <i class="fa-solid fa-chevron-down"></i>
                  </div>
                </div>
              </div>

              <div class="lessons-grid" style="${isOpen ? '' : 'display:none;'}">
                ${modLessons.map(lesson => {
                  const isDone = store.progress[lesson.id]?.is_completed;
                  const isVerseMemorized = store.progress[lesson.id]?.verse_memorized;
                  const pdfUrl = lesson.pdf_url || `/discipleship/Discipleship-Walk-Together-Ministries-Lesson-${lesson.overall_order}.pdf`;

                  return `
                    <div class="lesson-item-card ${isDone ? 'is-completed' : ''}" data-lesson-id="${lesson.id}" id="lesson-card-${lesson.id}">
                      <div>
                        <div class="lesson-card-header">
                          <span class="lesson-num">Lesson ${lesson.overall_order}</span>
                          ${isDone ? `
                            <span class="status-badge completed" title="Lesson Completed">
                              <i class="fa-solid fa-circle-check"></i> Complete
                            </span>
                          ` : `
                            <span class="status-badge" style="color:var(--text-muted);">
                              <i class="fa-regular fa-circle"></i> Incomplete
                            </span>
                          `}
                        </div>
                        <h4 class="lesson-card-title">${lesson.title}</h4>

                        ${lesson.verse_ref ? `
                          <div class="lesson-verse-pill" title="Memory Verse">
                            <i class="fa-solid fa-bookmark"></i>
                            <span>${lesson.verse_ref}</span>
                            ${isVerseMemorized ? '<i class="fa-solid fa-check" style="color:var(--accent-emerald); margin-left:2px;"></i>' : ''}
                          </div>
                        ` : ''}
                      </div>

                      <div class="lesson-card-footer">
                        <a href="${pdfUrl}" target="_blank" rel="noopener noreferrer" class="lesson-pdf-link" title="Open PDF Guide for Lesson ${lesson.overall_order}">
                          <i class="fa-regular fa-file-pdf" style="color:var(--accent-rose);"></i>
                          <span>PDF Guide</span>
                        </a>
                        <a href="#lesson/${lesson.id}" class="btn-primary" style="padding:0.4rem 0.9rem; font-size:0.82rem;">
                          ${isDone ? 'Review' : 'Start'} <i class="fa-solid fa-arrow-right" style="font-size:0.75rem;"></i>
                        </a>
                      </div>
                    </div>
                  `;
                }).join('')}
              </div>
            </div>
          `;
        }).join('')}
      </div>
    </div>
  `;

  // Attach accordion handlers
  const headers = container.querySelectorAll('[data-toggle-module]');
  headers.forEach(h => {
    h.onclick = () => {
      const card = h.closest('.module-card');
      const grid = card.querySelector('.lessons-grid');
      const isOpen = card.classList.contains('open');
      if (isOpen) {
        card.classList.remove('open');
        grid.style.display = 'none';
      } else {
        card.classList.add('open');
        grid.style.display = 'grid';
      }
    };
  });

  const expandBtn = container.querySelector('#expand-all-modules');
  if (expandBtn) {
    expandBtn.onclick = () => {
      container.querySelectorAll('.module-card').forEach(c => {
        c.classList.add('open');
        c.querySelector('.lessons-grid').style.display = 'grid';
      });
    };
  }

  const collapseBtn = container.querySelector('#collapse-all-modules');
  if (collapseBtn) {
    collapseBtn.onclick = () => {
      container.querySelectorAll('.module-card').forEach(c => {
        c.classList.remove('open');
        c.querySelector('.lessons-grid').style.display = 'none';
      });
    };
  }

  // Video modal
  const openVideoBtn = container.querySelector('#open-video-btn');
  if (openVideoBtn) {
    openVideoBtn.onclick = () => {
      const vRoot = document.getElementById('video-modal-root');
      if (!vRoot) return;
      vRoot.innerHTML = `
        <div class="modal-backdrop" id="video-backdrop">
          <div class="modal-card" style="max-width:800px; width:100%; padding:0; overflow:hidden;">
            <div style="display:flex; justify-content:space-between; align-items:center; padding:1rem 1.5rem; background:var(--bg-card);">
              <h3 style="font-family:var(--font-display); font-size:1.15rem; font-weight:700;">
                Walk Together Ministries - Course Introduction
              </h3>
              <button class="modal-close-btn" id="close-video-modal" aria-label="Close video">
                <i class="fa-solid fa-xmark"></i>
              </button>
            </div>
            <div style="position:relative; padding-bottom:56.25%; height:0; overflow:hidden; background:#000;">
              <iframe 
                src="https://www.youtube.com/embed/jTBBuirYxNo?autoplay=1" 
                title="Course Introduction Video" 
                frameborder="0" 
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
                allowfullscreen
                style="position:absolute; top:0; left:0; width:100%; height:100%;">
              </iframe>
            </div>
          </div>
        </div>
      `;
      const backdrop = document.getElementById('video-backdrop');
      const closeBtn = document.getElementById('close-video-modal');
      const close = () => { vRoot.innerHTML = ''; };
      backdrop.onclick = (e) => { if (e.target === backdrop) close(); };
      closeBtn.onclick = close;
    };
  }
}
